import type { BusinessProfileId } from '../../config/businessProfiles';
import type { BranchInventory, CatalogSnapshot, Category, InventoryMovement, InventoryMovementType, Product, ProductVariant, ProductWithInventory } from '../../types/domain';
import { seedCatalog } from './seed';
import { assertWritableForBusiness } from '../saas/saas-store';

export const CATALOG_DATABASE_KEY = 'nexo.phase3.catalog.database';
const DATABASE_KEY = CATALOG_DATABASE_KEY;
export const CATALOG_CHANGE_EVENT = 'nexo:catalog-changed';
const CHANGE_EVENT = CATALOG_CHANGE_EVENT;

type CatalogDatabase = { version: 1; catalogs: Record<string, CatalogSnapshot> };
export type ProductDraft = Omit<Product, 'id' | 'businessId' | 'category' | 'createdAt' | 'updatedAt'> & { id?: string; category?: string };
export type InventoryAdjustment = { productId: string; branchId: string; quantity: number; type: InventoryMovementType; reason: string; reference?: string; createdBy: string };

const id = (prefix: string) => `${prefix}_${crypto.randomUUID?.() ?? `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`}`;
const nowIso = () => new Date().toISOString();
const emptyDatabase = (): CatalogDatabase => ({ version: 1, catalogs: {} });

const genericCategoryPresets = [
  ['Productos', '#607d74'],
  ['Servicios', '#4d6f91'],
  ['Accesorios', '#8a6f91'],
  ['Paquetes y combos', '#72835d'],
  ['Productos digitales', '#5e7395'],
  ['Otros', '#777777'],
] as const;

const legacyCustomDemoProductIds = new Set(['prod_americano', 'prod_latte', 'prod_sandwich', 'prod_croissant', 'prod_special', 'prod_imported_order']);

const withoutLegacyCustomDemo = (catalog: CatalogSnapshot): CatalogSnapshot => {
  if (catalog.profileId !== 'custom' || !catalog.products.some((item) => legacyCustomDemoProductIds.has(item.id))) return catalog;
  const removed = new Set(catalog.products.filter((item) => legacyCustomDemoProductIds.has(item.id)).map((item) => item.id));
  return {
    ...catalog,
    products: catalog.products.filter((item) => !removed.has(item.id)),
    variants: catalog.variants.filter((item) => !removed.has(item.productId)),
    inventory: catalog.inventory.filter((item) => !removed.has(item.productId)),
    movements: catalog.movements.filter((item) => !removed.has(item.productId)),
  };
};

const withGenericCategories = (catalog: CatalogSnapshot): CatalogSnapshot => {
  const existing = new Set(catalog.categories.map((item) => item.name.trim().toLowerCase()));
  const additions = genericCategoryPresets
    .filter(([name]) => !existing.has(name.toLowerCase()))
    .map(([name, color], index) => ({
      id: id('cat'), businessId: catalog.businessId, name,
      slug: name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
      color, sortOrder: catalog.categories.length + index + 1, active: true, createdAt: nowIso(),
    }));
  return additions.length ? { ...catalog, categories: [...catalog.categories, ...additions] } : catalog;
};

const readDatabase = (): CatalogDatabase => {
  const raw = localStorage.getItem(DATABASE_KEY);
  if (!raw) return emptyDatabase();
  try {
    const parsed = JSON.parse(raw) as CatalogDatabase;
    return parsed.version === 1 && parsed.catalogs ? parsed : emptyDatabase();
  } catch {
    return emptyDatabase();
  }
};

const writeDatabase = (database: CatalogDatabase) => {
  localStorage.setItem(DATABASE_KEY, JSON.stringify(database));
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
};

export const ensureCatalog = (businessId: string, branchIds: string[], profileId: BusinessProfileId): CatalogSnapshot => {
  const database = readDatabase();
  if (!database.catalogs[businessId]) {
    database.catalogs[businessId] = withGenericCategories(seedCatalog(businessId, branchIds, profileId));
    writeDatabase(database);
  } else {
    const upgraded = withGenericCategories(withoutLegacyCustomDemo(database.catalogs[businessId]));
    if (upgraded !== database.catalogs[businessId]) {
      database.catalogs[businessId] = upgraded;
      writeDatabase(database);
    }
  }
  return database.catalogs[businessId];
};

export const getCatalog = (businessId: string): CatalogSnapshot | null => {
  const database = readDatabase();
  const current = database.catalogs[businessId];
  if (!current) return null;
  const upgraded = withGenericCategories(withoutLegacyCustomDemo(current));
  if (upgraded !== current) {
    database.catalogs[businessId] = upgraded;
    writeDatabase(database);
  }
  return upgraded;
};

export const subscribeCatalog = (listener: () => void) => {
  window.addEventListener(CHANGE_EVENT, listener);
  window.addEventListener('storage', listener);
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener);
    window.removeEventListener('storage', listener);
  };
};

const updateCatalog = (businessId: string, updater: (catalog: CatalogSnapshot) => CatalogSnapshot) => {
  assertWritableForBusiness(businessId);
  const database = readDatabase();
  const current = database.catalogs[businessId];
  if (!current) throw new Error('No se encontró el catálogo del negocio activo.');
  database.catalogs[businessId] = updater(current);
  writeDatabase(database);
  return database.catalogs[businessId];
};

export const saveCategory = (businessId: string, input: { id?: string; name: string; color: string }) => updateCatalog(businessId, (catalog) => {
  const timestamp = nowIso();
  const name = input.name.trim();
  if (!name) throw new Error('La categoría necesita un nombre.');
  if (catalog.categories.some((item) => item.name.toLowerCase() === name.toLowerCase() && item.id !== input.id)) throw new Error('Ya existe una categoría con ese nombre.');
  const slug = name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  if (input.id) return { ...catalog, categories: catalog.categories.map((item) => item.id === input.id ? { ...item, name, slug, color: input.color } : item) };
  const category: Category = { id: id('cat'), businessId, name, slug, color: input.color, sortOrder: catalog.categories.length + 1, active: true, createdAt: timestamp };
  return { ...catalog, categories: [...catalog.categories, category] };
});

export const saveProduct = (businessId: string, draft: ProductDraft) => updateCatalog(businessId, (catalog) => {
  const timestamp = nowIso();
  const category = catalog.categories.find((item) => item.id === draft.categoryId);
  if (!draft.name.trim()) throw new Error('El producto necesita un nombre.');
  if (!category) throw new Error('Selecciona una categoría válida.');
  if (draft.priceCents < 0 || draft.costCents < 0) throw new Error('Los importes no pueden ser negativos.');
  if (draft.sku && catalog.products.some((item) => item.sku === draft.sku && item.id !== draft.id && !item.archivedAt)) throw new Error('El SKU ya está asignado a otro producto.');
  if (draft.barcode && catalog.products.some((item) => item.barcode === draft.barcode && item.id !== draft.id && !item.archivedAt)) throw new Error('El código de barras ya está asignado.');
  if (draft.id) {
    return { ...catalog, products: catalog.products.map((item) => item.id === draft.id ? { ...item, ...draft, category: category.name, businessId, updatedAt: timestamp } as Product : item) };
  }
  const created: Product = { ...draft, id: id('prod'), businessId, category: category.name, createdAt: timestamp, updatedAt: timestamp };
  return { ...catalog, products: [...catalog.products, created] };
});

export const duplicateProduct = (businessId: string, productId: string) => updateCatalog(businessId, (catalog) => {
  const original = catalog.products.find((item) => item.id === productId);
  if (!original) throw new Error('No se encontró el producto.');
  const timestamp = nowIso();
  const copyId = id('prod');
  const copy: Product = { ...original, id: copyId, name: `${original.name} — copia`, sku: original.sku ? `${original.sku}-COPIA` : '', barcode: '', featured: false, createdAt: timestamp, updatedAt: timestamp };
  const variants = catalog.variants.filter((item) => item.productId === productId).map((variant) => ({ ...variant, id: id('var'), productId: copyId, sku: variant.sku ? `${variant.sku}-C` : '', barcode: '' }));
  return { ...catalog, products: [...catalog.products, copy], variants: [...catalog.variants, ...variants] };
});

export const setProductArchived = (businessId: string, productId: string, archived: boolean) => updateCatalog(businessId, (catalog) => ({
  ...catalog,
  products: catalog.products.map((item) => item.id === productId ? { ...item, active: archived ? false : item.active, archivedAt: archived ? nowIso() : undefined, updatedAt: nowIso() } : item),
}));

export const saveVariant = (businessId: string, productId: string, input: Omit<ProductVariant, 'id' | 'productId'> & { id?: string }) => updateCatalog(businessId, (catalog) => {
  if (!catalog.products.some((item) => item.id === productId)) throw new Error('No se encontró el producto de la variante.');
  if (!input.name.trim()) throw new Error('La variante necesita un nombre.');
  if (input.id) return { ...catalog, variants: catalog.variants.map((item) => item.id === input.id ? { ...item, ...input, productId } as ProductVariant : item) };
  return { ...catalog, variants: [...catalog.variants, { ...input, id: id('var'), productId }] };
});

export const adjustInventory = (businessId: string, input: InventoryAdjustment) => updateCatalog(businessId, (catalog) => {
  const product = catalog.products.find((item) => item.id === input.productId);
  if (!product) throw new Error('No se encontró el producto.');
  if (!product.trackInventory) throw new Error('Este producto no controla inventario físico.');
  if (!input.reason.trim()) throw new Error('Escribe el motivo del movimiento.');
  const current = catalog.inventory.find((row) => row.productId === input.productId && row.branchId === input.branchId);
  const beforeQty = current?.onHand ?? 0;
  const absoluteTypes = new Set<InventoryMovementType>(['count']);
  const afterQty = absoluteTypes.has(input.type) ? input.quantity : beforeQty + input.quantity;
  if (afterQty < 0) throw new Error(`La existencia no puede quedar negativa. Disponible: ${beforeQty}.`);
  const timestamp = nowIso();
  const inventoryRow: BranchInventory = current
    ? { ...current, onHand: afterQty, updatedAt: timestamp }
    : { id: id('inv'), businessId, branchId: input.branchId, productId: input.productId, onHand: afterQty, reserved: 0, inTransit: 0, updatedAt: timestamp };
  const movement: InventoryMovement = { id: id('mov'), businessId, branchId: input.branchId, productId: input.productId, type: input.type, quantity: afterQty - beforeQty, beforeQty, afterQty, reason: input.reason.trim(), reference: input.reference?.trim(), createdBy: input.createdBy, createdAt: timestamp };
  return { ...catalog, inventory: [...catalog.inventory.filter((row) => !(row.productId === input.productId && row.branchId === input.branchId)), inventoryRow], movements: [movement, ...catalog.movements] };
});


export type InventoryBatchChange = {
  productId: string;
  branchId: string;
  onHandDelta?: number;
  reservedDelta?: number;
  inTransitDelta?: number;
  type: InventoryMovementType;
  reason: string;
  reference?: string;
  createdBy: string;
};

export const applyInventoryBatch = (businessId: string, changes: InventoryBatchChange[]) => updateCatalog(businessId, (catalog) => {
  const inventory = [...catalog.inventory];
  const movements: InventoryMovement[] = [];
  const timestamp = nowIso();
  for (const change of changes) {
    const product = catalog.products.find((item) => item.id === change.productId);
    if (!product) throw new Error('No se encontró uno de los productos de la operación.');
    if (!product.trackInventory) continue;
    const index = inventory.findIndex((row) => row.productId === change.productId && row.branchId === change.branchId);
    const current = index >= 0 ? inventory[index] : undefined;
    const beforeQty = current?.onHand ?? 0;
    const beforeReserved = current?.reserved ?? 0;
    const beforeTransit = current?.inTransit ?? 0;
    const afterQty = beforeQty + (change.onHandDelta ?? 0);
    const afterReserved = beforeReserved + (change.reservedDelta ?? 0);
    const afterTransit = beforeTransit + (change.inTransitDelta ?? 0);
    if (afterQty < 0) throw new Error(`Existencia insuficiente para ${product.name}. Disponible físico: ${beforeQty}.`);
    if (afterReserved < 0) throw new Error(`La reserva de ${product.name} no puede ser negativa.`);
    if (afterReserved > afterQty) throw new Error(`No hay disponibilidad para reservar ${product.name}. Disponible: ${Math.max(0, beforeQty - beforeReserved)}.`);
    if (afterTransit < 0) throw new Error(`El inventario en tránsito de ${product.name} no puede ser negativo.`);
    const row: BranchInventory = current
      ? { ...current, onHand: afterQty, reserved: afterReserved, inTransit: afterTransit, updatedAt: timestamp }
      : { id: id('inv'), businessId, branchId: change.branchId, productId: change.productId, onHand: afterQty, reserved: afterReserved, inTransit: afterTransit, updatedAt: timestamp };
    if (index >= 0) inventory[index] = row; else inventory.push(row);
    movements.push({
      id: id('mov'), businessId, branchId: change.branchId, productId: change.productId, type: change.type,
      quantity: afterQty - beforeQty, beforeQty, afterQty, beforeReserved, afterReserved,
      reason: change.reason, reference: change.reference, createdBy: change.createdBy, createdAt: timestamp,
    });
  }
  return { ...catalog, inventory, movements: [...movements.reverse(), ...catalog.movements] };
});

export const notifyCatalogChanged = () => window.dispatchEvent(new CustomEvent(CHANGE_EVENT));

const csvEscape = (value: unknown) => `"${String(value ?? '').replaceAll('"', '""')}"`;
export const exportProductsCsv = (products: Product[]) => {
  const headers = ['nombre', 'descripcion', 'categoria', 'tipo', 'sku', 'codigo_barras', 'unidad', 'precio', 'costo', 'mayoreo', 'impuesto', 'abastecimiento', 'visible_pos', 'visible_catalogo'];
  const rows = products.map((item) => [item.name, item.description, item.category, item.type, item.sku, item.barcode, item.unit, (item.priceCents / 100).toFixed(2), (item.costCents / 100).toFixed(2), (item.wholesalePriceCents / 100).toFixed(2), item.taxRate, item.supplyType, item.visiblePos, item.visibleCatalog].map(csvEscape).join(','));
  return `\uFEFF${headers.join(',')}\n${rows.join('\n')}`;
};

const parseCsvLine = (line: string) => {
  const cells: string[] = [];
  let cell = '';
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"' && quoted && line[index + 1] === '"') { cell += '"'; index += 1; }
    else if (character === '"') quoted = !quoted;
    else if (character === ',' && !quoted) { cells.push(cell.trim()); cell = ''; }
    else cell += character;
  }
  cells.push(cell.trim());
  return cells;
};

export const importProductsCsv = (businessId: string, branchId: string, csv: string, createdBy: string) => {
  const lines = csv.replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) throw new Error('El CSV no contiene filas de productos.');
  const headers = parseCsvLine(lines[0]).map((item) => item.toLowerCase());
  const required = ['nombre', 'categoria', 'precio'];
  required.forEach((header) => { if (!headers.includes(header)) throw new Error(`Falta la columna obligatoria: ${header}.`); });
  let imported = 0;
  let skipped = 0;
  let catalog = getCatalog(businessId);
  if (!catalog) throw new Error('No se encontró el catálogo activo.');
  lines.slice(1).forEach((line) => {
    const values = parseCsvLine(line);
    const row = Object.fromEntries(headers.map((header, index) => [header, values[index] ?? '']));
    const name = row.nombre?.trim();
    const categoryName = row.categoria?.trim();
    const price = Number(row.precio);
    if (!name || !categoryName || !Number.isFinite(price)) { skipped += 1; return; }
    let category = catalog!.categories.find((item) => item.name.toLowerCase() === categoryName.toLowerCase());
    if (!category) {
      saveCategory(businessId, { name: categoryName, color: '#70857d' });
      catalog = getCatalog(businessId)!;
      category = catalog.categories.find((item) => item.name.toLowerCase() === categoryName.toLowerCase());
    }
    const supplyType = (row.abastecimiento || 'stock') as Product['supplyType'];
    saveProduct(businessId, {
      name, description: row.descripcion ?? '', type: row.tipo === 'service' ? 'service' : 'product', categoryId: category!.id, brand: '', sku: row.sku ?? '', barcode: row.codigo_barras ?? '', unit: row.unidad || 'pieza', priceCents: Math.round(price * 100), wholesalePriceCents: Math.round(Number(row.mayoreo || 0) * 100), costCents: Math.round(Number(row.costo || 0) * 100), taxRate: Number(row.impuesto || 16), accent: category!.color, emoji: name.slice(0, 2).toUpperCase(), active: true, visiblePos: row.visible_pos !== 'false', visibleCatalog: row.visible_catalogo !== 'false', allowOrders: true, featured: false, trackInventory: supplyType === 'stock' || supplyType === 'mixed', minStock: 0, maxStock: 0, supplyType, leadTime: '', depositPercent: 0,
    });
    const latest = getCatalog(businessId)!;
    const created = latest.products[latest.products.length - 1];
    if (created.trackInventory) adjustInventory(businessId, { productId: created.id, branchId, quantity: 0, type: 'count', reason: 'Alta mediante importación CSV', createdBy });
    catalog = getCatalog(businessId)!;
    imported += 1;
  });
  return { imported, skipped };
};

export const productWithInventory = (catalog: CatalogSnapshot, branchId: string): ProductWithInventory[] => catalog.products.map((product) => {
  const inventory = catalog.inventory.find((row) => row.productId === product.id && row.branchId === branchId);
  const stock = inventory?.onHand ?? 0;
  const reserved = inventory?.reserved ?? 0;
  return { ...product, stock, reserved, inTransit: inventory?.inTransit ?? 0, available: Math.max(0, stock - reserved), variants: catalog.variants.filter((item) => item.productId === product.id && item.active) };
});
